import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Paperclip, Upload, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "@/lib/api";
import { uploadFile, validateFile } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { ALLOWED_UPLOAD_EXTENSIONS, formatBytes, fmtDate } from "@/lib/constants";

export default function AttachmentsCard({
  basePath,
  scope = "formula",
  emptyText = "No files attached. Add supplier spec sheets, COAs or product photos for this formula.",
}) {
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const { data: attachments = [], refetch } = useQuery({
    queryKey: ["attachments", basePath],
    queryFn: async () => (await api.get(`${basePath}/attachments`)).data,
  });

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = validateFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setProgress(1);
    try {
      const uploaded = await uploadFile(file, setProgress);
      await api.post(`${basePath}/attachments`, {
        file_id: uploaded.file_id,
        title: uploaded.original_filename,
      });
      toast.success(`${uploaded.original_filename} attached`);
      refetch();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProgress(null);
    }
  };

  const download = async (a) => {
    try {
      await downloadFile(
        `${basePath}/attachments/${a.id}/download`,
        a.file.original_filename,
        {},
        a.file.content_type
      );
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (a) => {
    const ok = await confirm({
      title: "Remove attachment",
      message: `Remove "${a.title}" from this ${scope}? The file will no longer be accessible.`,
      confirmLabel: "Remove attachment",
    });
    if (!ok) return;
    try {
      await api.delete(`${basePath}/attachments/${a.id}`);
      toast.success("Attachment removed");
      refetch();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="attachments-card">
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#F4EFEA]">
        <div className="flex items-center gap-2">
          <Paperclip size={16} className="text-[#C5A059]" />
          <h2 className="font-display text-lg font-semibold text-[#1C100B]">Attachments ({attachments.length})</h2>
        </div>
        {canWrite && (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={pick}
              accept={ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(",")}
              data-testid="attachment-file-input"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={progress != null}
              className="bg-white hover:bg-[#FAF6F0] text-[#2A1810] border border-[#E8DFD1] hover:border-[#C5A059] px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="attachment-upload-button"
            >
              <Upload size={14} /> Attach file
            </button>
          </>
        )}
      </div>

      <div className="px-6 py-4 space-y-3">
        {progress != null && (
          <div data-testid="attachment-upload-progress">
            <div className="flex justify-between text-xs text-[#5C4A3E] mb-2">
              <span>Uploading…</span>
              <span className="font-semibold">{progress}%</span>
            </div>
            <div className="h-1.5 bg-[#F4EFEA] rounded-full overflow-hidden">
              <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {attachments.map((a) => (
          <div
            key={a.id}
            className="flex items-center justify-between gap-3 border border-[#F4EFEA] rounded-lg px-4 py-3"
            data-testid={`attachment-row-${a.id}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#2A1810] truncate">{a.title}</div>
              <div className="text-xs text-[#8C7A6B] mt-0.5">
                {formatBytes(a.file.size)} · {a.created_by_name || "—"} · {fmtDate(a.created_at)}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => download(a)} className="p-2 rounded hover:bg-[#FAF6F0] text-[#5C4A3E]" data-testid={`download-attachment-${a.id}`}>
                <Download size={14} />
              </button>
              {canWrite && (
                <button onClick={() => remove(a)} className="p-2 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`delete-attachment-${a.id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}

        {!attachments.length && progress == null && (
          <p className="text-sm text-[#8C7A6B]" data-testid="attachments-empty">{emptyText}</p>
        )}
      </div>
    </div>
  );
}
